import { collection, addDoc, onSnapshot, query, deleteDoc, doc, type Firestore, type Unsubscribe, type QuerySnapshot, type DocumentData, type QueryDocumentSnapshot } from 'firebase/firestore';
import type { Holiday } from './types';
import { calculateDuration } from './utils';
import { EMAIL_RECIPIENT } from './config';
import { formatDate } from './utils';

/**
 * Returns the Firestore collection reference for holidays (root collection 'holiday').
 */
export function getHolidayCollectionRef(db: Firestore) {
  return collection(db, 'holiday');
}

/**
 * Adds a new holiday to Firestore.
 */
export async function addHoliday(
  db: Firestore,
  name: string,
  startDate: string,
  endDate: string
): Promise<void> {
  const duration = calculateDuration(startDate, endDate);
  const holidaysCollectionRef = getHolidayCollectionRef(db);

  await addDoc(holidaysCollectionRef, {
    name,
    startDate,
    endDate,
    duration: duration.durationText,
    loggedAt: Date.now()
  });
}

/**
 * Deletes a holiday document from Firestore.
 */
export async function deleteHolidayById(db: Firestore, docId: string): Promise<void> {
  const holidaysCollectionRef = getHolidayCollectionRef(db);
  const docRef = doc(holidaysCollectionRef, docId);
  await deleteDoc(docRef);
}

/**
 * Sets up a real-time listener for holiday data.
 */
export function setupHolidayListener(
  db: Firestore,
  onUpdate: (holidays: Holiday[]) => void,
  onError: (error: Error) => void
): Unsubscribe {
  const holidaysCollectionRef = getHolidayCollectionRef(db);
  const q = query(holidaysCollectionRef);

  return onSnapshot(
    q,
    (querySnapshot: QuerySnapshot<DocumentData>) => {
      const holidays: Holiday[] = [];
      querySnapshot.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
        holidays.push({ id: doc.id, ...doc.data() } as Holiday);
      });
      onUpdate(holidays);
    },
    (error: Error) => {
      onError(error);
    }
  );
}

/**
 * Simulates sending an email by logging the payload to the console.
 */
export async function sendHolidayEmail(
  name: string,
  startDate: string,
  endDate: string,
  durationText: string,
  userId: string | null
): Promise<void> {
  const emailSubject = `Holiday Logged for ${name}: ${formatDate(startDate)} to ${formatDate(endDate)}`;
  const emailBody = `
    A new holiday has been logged for **${name}** by user ${userId}.

    **Details:**
    - Employee Name: ${name}
    - Start Date: ${formatDate(startDate)}
    - Return Date: ${formatDate(endDate)}
    - **Duration: ${durationText}**
   
    This email was intended for: ${EMAIL_RECIPIENT}
   
    ---
    (NOTE: This is a client-side simulation. No actual email was sent.)
  `;

  console.groupCollapsed("📧 Email Simulation: New Holiday Logged");
  console.log(`To: ${EMAIL_RECIPIENT}`);
  console.log(`Subject: ${emailSubject}`);
  console.log("Body:", emailBody);
  console.groupEnd();
}

